import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useTenant } from "../lib/tenant-context";

interface Brand {
  id: string;
  name: string;
  slug: string;
  primary_color: string;
  voice_name: string;
  voice_tone: string;
  trade_in_enabled: number;
  store_count: number;
}

interface Store {
  id: string;
  brand_id: string;
  brand_name: string;
  brand_slug: string;
  brand_color: string;
  name: string;
  store_code: string;
  city: string;
  state: string;
  pin_enabled: number;
  session_count: number;
}

// Tenant-specific demo stores data
function getTenantDemoStoresData(tenantId: string): {
  brands: Brand[];
  stores: Store[];
} {
  if (tenantId === "arcteryx") {
    const brands: Brand[] = [
      {
        id: "brand-arcteryx",
        name: "Arc'teryx",
        slug: "arcteryx",
        primary_color: "#1a1a1a",
        voice_name: "Alpine",
        voice_tone: "professional",
        trade_in_enabled: 1,
        store_count: 4,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-arc-van",
        brand_id: "brand-arcteryx",
        brand_name: "Arc'teryx",
        brand_slug: "arcteryx",
        brand_color: "#1a1a1a",
        name: "Vancouver Flagship",
        store_code: "ARC-VAN-001",
        city: "Vancouver",
        state: "BC",
        pin_enabled: 1,
        session_count: 186,
      },
      {
        id: "store-arc-whi",
        brand_id: "brand-arcteryx",
        brand_name: "Arc'teryx",
        brand_slug: "arcteryx",
        brand_color: "#1a1a1a",
        name: "Whistler Village",
        store_code: "ARC-WHI-001",
        city: "Whistler",
        state: "BC",
        pin_enabled: 1,
        session_count: 124,
      },
      {
        id: "store-arc-sea",
        brand_id: "brand-arcteryx",
        brand_name: "Arc'teryx",
        brand_slug: "arcteryx",
        brand_color: "#1a1a1a",
        name: "Seattle Premium",
        store_code: "ARC-SEA-001",
        city: "Seattle",
        state: "WA",
        pin_enabled: 1,
        session_count: 98,
      },
      {
        id: "store-arc-nyc",
        brand_id: "brand-arcteryx",
        brand_name: "Arc'teryx",
        brand_slug: "arcteryx",
        brand_color: "#1a1a1a",
        name: "NYC SoHo",
        store_code: "ARC-NYC-001",
        city: "New York",
        state: "NY",
        pin_enabled: 0,
        session_count: 0,
      },
    ];
    return { brands, stores };
  } else if (tenantId === "nespresso") {
    const brands: Brand[] = [
      {
        id: "brand-nespresso",
        name: "Nespresso",
        slug: "nespresso",
        primary_color: "#6B4423",
        voice_name: "Nina",
        voice_tone: "luxury",
        trade_in_enabled: 1,
        store_count: 2,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-nes-nyc",
        brand_id: "brand-nespresso",
        brand_name: "Nespresso",
        brand_slug: "nespresso",
        brand_color: "#6B4423",
        name: "Madison Avenue Boutique",
        store_code: "NES-NYC-001",
        city: "New York",
        state: "NY",
        pin_enabled: 1,
        session_count: 312,
      },
      {
        id: "store-nes-la",
        brand_id: "brand-nespresso",
        brand_name: "Nespresso",
        brand_slug: "nespresso",
        brand_color: "#6B4423",
        name: "Beverly Hills",
        store_code: "NES-LA-001",
        city: "Los Angeles",
        state: "CA",
        pin_enabled: 1,
        session_count: 256,
      },
    ];
    return { brands, stores };
  } else if (tenantId === "lego") {
    const brands: Brand[] = [
      {
        id: "brand-lego",
        name: "LEGO",
        slug: "lego",
        primary_color: "#FFD500",
        voice_name: "Brick",
        voice_tone: "playful",
        trade_in_enabled: 1,
        store_count: 5,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-lego-nyc",
        brand_id: "brand-lego",
        brand_name: "LEGO",
        brand_slug: "lego",
        brand_color: "#FFD500",
        name: "LEGO Store Rockefeller Center",
        store_code: "LEGO-NYC-001",
        city: "New York",
        state: "NY",
        pin_enabled: 1,
        session_count: 423,
      },
      {
        id: "store-lego-dis",
        brand_id: "brand-lego",
        brand_name: "LEGO",
        brand_slug: "lego",
        brand_color: "#FFD500",
        name: "LEGO Store Disney Springs",
        store_code: "LEGO-ORL-001",
        city: "Orlando",
        state: "FL",
        pin_enabled: 1,
        session_count: 567,
      },
      {
        id: "store-lego-ana",
        brand_id: "brand-lego",
        brand_name: "LEGO",
        brand_slug: "lego",
        brand_color: "#FFD500",
        name: "LEGO Store Downtown Disney",
        store_code: "LEGO-ANA-001",
        city: "Anaheim",
        state: "CA",
        pin_enabled: 1,
        session_count: 489,
      },
      {
        id: "store-lego-moa",
        brand_id: "brand-lego",
        brand_name: "LEGO",
        brand_slug: "lego",
        brand_color: "#FFD500",
        name: "LEGO Store Mall of America",
        store_code: "LEGO-MIN-001",
        city: "Minneapolis",
        state: "MN",
        pin_enabled: 1,
        session_count: 356,
      },
      {
        id: "store-lego-lon",
        brand_id: "brand-lego",
        brand_name: "LEGO",
        brand_slug: "lego",
        brand_color: "#FFD500",
        name: "LEGO Store Leicester Square",
        store_code: "LEGO-LON-001",
        city: "London",
        state: "UK",
        pin_enabled: 1,
        session_count: 612,
      },
    ];
    return { brands, stores };
  } else if (tenantId === "nike") {
    const brands: Brand[] = [
      {
        id: "brand-nike",
        name: "Nike",
        slug: "nike",
        primary_color: "#111111",
        voice_name: "Jordan",
        voice_tone: "energetic",
        trade_in_enabled: 1,
        store_count: 4,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-nike-nyc",
        brand_id: "brand-nike",
        brand_name: "Nike",
        brand_slug: "nike",
        brand_color: "#111111",
        name: "House of Innovation NYC",
        store_code: "NIKE-NYC-001",
        city: "New York",
        state: "NY",
        pin_enabled: 1,
        session_count: 234,
      },
      {
        id: "store-nike-soho",
        brand_id: "brand-nike",
        brand_name: "Nike",
        brand_slug: "nike",
        brand_color: "#111111",
        name: "Nike SoHo",
        store_code: "NIKE-NYC-002",
        city: "New York",
        state: "NY",
        pin_enabled: 1,
        session_count: 156,
      },
      {
        id: "store-nike-grove",
        brand_id: "brand-nike",
        brand_name: "Nike",
        brand_slug: "nike",
        brand_color: "#111111",
        name: "Nike The Grove",
        store_code: "NIKE-LA-001",
        city: "Los Angeles",
        state: "CA",
        pin_enabled: 1,
        session_count: 189,
      },
      {
        id: "store-nike-chi",
        brand_id: "brand-nike",
        brand_name: "Nike",
        brand_slug: "nike",
        brand_color: "#111111",
        name: "Nike Michigan Avenue",
        store_code: "NIKE-CHI-001",
        city: "Chicago",
        state: "IL",
        pin_enabled: 1,
        session_count: 145,
      },
    ];
    return { brands, stores };
  } else if (tenantId === "demobrand") {
    const brands: Brand[] = [
      {
        id: "brd_demobrand",
        name: "Demo Brand",
        slug: "demobrand",
        primary_color: "#0066CC",
        voice_name: "Coach",
        voice_tone: "motivational",
        trade_in_enabled: 1,
        store_count: 1,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-demo-001",
        brand_id: "brd_demobrand",
        brand_name: "Demo Brand",
        brand_slug: "demobrand",
        brand_color: "#0066CC",
        name: "Demo Store",
        store_code: "DEMO-001",
        city: "Demo City",
        state: "DC",
        pin_enabled: 1,
        session_count: 47,
      },
    ];
    return { brands, stores };
  }

  // Default: return empty for unknown tenants
  if (tenantId === "lululemon") {
    const brands: Brand[] = [
      {
        id: "brand-lululemon",
        name: "lululemon",
        slug: "lululemon",
        primary_color: "#C41230",
        voice_name: "Luna",
        voice_tone: "friendly",
        trade_in_enabled: 1,
        store_count: 3,
      },
    ];
    const stores: Store[] = [
      {
        id: "store-lulu-nj",
        brand_id: "brand-lululemon",
        brand_name: "lululemon",
        brand_slug: "lululemon",
        brand_color: "#C41230",
        name: "Short Hills Mall",
        store_code: "LULU-NJ-001",
        city: "Short Hills",
        state: "NJ",
        pin_enabled: 1,
        session_count: 47,
      },
      {
        id: "store-lulu-nyc",
        brand_id: "brand-lululemon",
        brand_name: "lululemon",
        brand_slug: "lululemon",
        brand_color: "#C41230",
        name: "Fifth Avenue Flagship",
        store_code: "LULU-NYC-001",
        city: "New York",
        state: "NY",
        pin_enabled: 1,
        session_count: 89,
      },
      {
        id: "store-lulu-la",
        brand_id: "brand-lululemon",
        brand_name: "lululemon",
        brand_slug: "lululemon",
        brand_color: "#C41230",
        name: "Santa Monica",
        store_code: "LULU-LA-001",
        city: "Santa Monica",
        state: "CA",
        pin_enabled: 0,
        session_count: 0,
      },
    ];
    return { brands, stores };
  }

  // Unknown tenant - return empty
  return { brands: [], stores: [] };
}

export const Route = createFileRoute("/stores")({
  component: StoresConfigPage,
});

function StoresConfigPage() {
  const { currentTenant } = useTenant();
  const [brands, setBrands] = useState<Brand[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"brands" | "stores">("brands");
  const [showCreateBrand, setShowCreateBrand] = useState(false);
  const [showCreateStore, setShowCreateStore] = useState(false);

  useEffect(() => {
    loadData();
  }, [currentTenant.id]);

  const loadData = async () => {
    try {
      const [brandsRes, storesRes] = await Promise.all([
        fetch(`/api/v1/config/brands?tenantId=${currentTenant.id}`),
        fetch(`/api/v1/config/stores?tenantId=${currentTenant.id}`),
      ]);

      let hasBrands = false;
      let hasStores = false;

      if (brandsRes.ok) {
        const contentType = brandsRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await brandsRes.json();
          if (data.brands && data.brands.length > 0) {
            setBrands(data.brands);
            hasBrands = true;
          }
        }
      }

      if (storesRes.ok) {
        const contentType = storesRes.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await storesRes.json();
          if (data.stores && data.stores.length > 0) {
            setStores(data.stores);
            hasStores = true;
          }
        }
      }

      // Use demo data if API returns nothing
      if (!hasBrands || !hasStores) {
        const demoData = getTenantDemoStoresData(currentTenant.id);
        if (!hasBrands) setBrands(demoData.brands);
        if (!hasStores) setStores(demoData.stores);
      }
    } catch (error) {
      console.debug("Failed to load config, using demo data:", error);
      const demoData = getTenantDemoStoresData(currentTenant.id);
      setBrands(demoData.brands);
      setStores(demoData.stores);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-light mb-2" style={{ color: "#171717" }}>
          Store Configuration
        </h1>
        <p className="text-sm" style={{ color: "#A9A9A9" }}>
          Configure brands, stores, and AI Educator settings
        </p>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-4 mb-6 border-b"
        style={{ borderColor: "rgba(169,169,169,0.2)" }}
      >
        <button
          onClick={() => setActiveTab("brands")}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === "brands" ? "#00FBA9" : "transparent",
            color: activeTab === "brands" ? "#171717" : "#A9A9A9",
          }}
        >
          Brands ({brands.length})
        </button>
        <button
          onClick={() => setActiveTab("stores")}
          className="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
          style={{
            borderColor: activeTab === "stores" ? "#00FBA9" : "transparent",
            color: activeTab === "stores" ? "#171717" : "#A9A9A9",
          }}
        >
          Stores ({stores.length})
        </button>
      </div>

      {loading ? (
        <div className="p-12 text-center">
          <div
            className="animate-spin w-8 h-8 border-2 border-t-transparent rounded-full mx-auto mb-4"
            style={{ borderColor: "#00FBA9" }}
          />
        </div>
      ) : activeTab === "brands" ? (
        <>
          {/* Brands List */}
          <div className="flex justify-between items-center mb-4">
            <h2
              className="text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              BRANDS
            </h2>
            <button
              onClick={() => setShowCreateBrand(true)}
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              Add Brand
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {brands.map((brand) => (
              <Link
                key={brand.id}
                to="/stores/$id"
                params={{ id: brand.id }}
                className="bg-white border p-5 hover:shadow-md transition-shadow"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: brand.primary_color }}
                    >
                      {brand.name[0]}
                    </div>
                    <div>
                      <h3 className="font-medium" style={{ color: "#171717" }}>
                        {brand.name}
                      </h3>
                      <p className="text-xs" style={{ color: "#A9A9A9" }}>
                        {brand.slug}
                      </p>
                    </div>
                  </div>
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: brand.trade_in_enabled
                        ? "#00FBA9"
                        : "#A9A9A9",
                    }}
                    title={
                      brand.trade_in_enabled
                        ? "Trade-in enabled"
                        : "Trade-in disabled"
                    }
                  />
                </div>

                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p
                      className="text-lg font-light"
                      style={{ color: "#171717" }}
                    >
                      {brand.store_count}
                    </p>
                    <p className="text-xs" style={{ color: "#A9A9A9" }}>
                      Stores
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-lg font-light capitalize"
                      style={{ color: "#171717" }}
                    >
                      {brand.voice_tone}
                    </p>
                    <p className="text-xs" style={{ color: "#A9A9A9" }}>
                      Voice Tone
                    </p>
                  </div>
                  <div>
                    <p
                      className="text-lg font-light"
                      style={{ color: "#171717" }}
                    >
                      {brand.voice_name}
                    </p>
                    <p className="text-xs" style={{ color: "#A9A9A9" }}>
                      AI Name
                    </p>
                  </div>
                </div>
              </Link>
            ))}

            {brands.length === 0 && (
              <div
                className="col-span-2 bg-white border p-12 text-center"
                style={{ borderColor: "rgba(169,169,169,0.2)" }}
              >
                <p className="mb-4" style={{ color: "#A9A9A9" }}>
                  No brands configured yet
                </p>
                <button
                  onClick={() => setShowCreateBrand(true)}
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: "#00FBA9", color: "#171717" }}
                >
                  Add Your First Brand
                </button>
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* Stores List */}
          <div className="flex justify-between items-center mb-4">
            <h2
              className="text-xs font-medium tracking-wider"
              style={{ color: "#A9A9A9" }}
            >
              STORES
            </h2>
            <button
              onClick={() => setShowCreateStore(true)}
              className="px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
              disabled={brands.length === 0}
            >
              Add Store
            </button>
          </div>

          <div
            className="bg-white border overflow-hidden"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <table className="w-full">
              <thead>
                <tr
                  className="border-b"
                  style={{ borderColor: "rgba(169,169,169,0.1)" }}
                >
                  <th
                    className="text-left text-xs font-medium px-6 py-3"
                    style={{ color: "#A9A9A9" }}
                  >
                    STORE
                  </th>
                  <th
                    className="text-left text-xs font-medium px-4 py-3"
                    style={{ color: "#A9A9A9" }}
                  >
                    BRAND
                  </th>
                  <th
                    className="text-left text-xs font-medium px-4 py-3"
                    style={{ color: "#A9A9A9" }}
                  >
                    LOCATION
                  </th>
                  <th
                    className="text-center text-xs font-medium px-4 py-3"
                    style={{ color: "#A9A9A9" }}
                  >
                    PIN
                  </th>
                  <th
                    className="text-right text-xs font-medium px-6 py-3"
                    style={{ color: "#A9A9A9" }}
                  >
                    SESSIONS
                  </th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => (
                  <Link
                    key={store.id}
                    to="/stores/store/$id"
                    params={{ id: store.id }}
                    className="table-row border-b hover:bg-gray-50 transition-colors cursor-pointer"
                    style={{ borderColor: "rgba(169,169,169,0.05)" }}
                  >
                    <td className="px-6 py-4">
                      <p
                        className="font-medium text-sm"
                        style={{ color: "#171717" }}
                      >
                        {store.name}
                      </p>
                      {store.store_code && (
                        <p className="text-xs" style={{ color: "#A9A9A9" }}>
                          {store.store_code}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: store.brand_color }}
                        />
                        <span className="text-sm" style={{ color: "#171717" }}>
                          {store.brand_name}
                        </span>
                      </div>
                    </td>
                    <td
                      className="px-4 py-4 text-sm"
                      style={{ color: "#A9A9A9" }}
                    >
                      {store.city}, {store.state}
                    </td>
                    <td className="px-4 py-4 text-center">
                      <span
                        className="inline-flex items-center justify-center w-6 h-6 rounded-full"
                        style={{
                          backgroundColor: store.pin_enabled
                            ? "rgba(0,251,169,0.15)"
                            : "rgba(169,169,169,0.15)",
                        }}
                      >
                        {store.pin_enabled ? (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#00FBA9"
                            strokeWidth="2"
                          >
                            <path
                              d="M20 6L9 17l-5-5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        ) : (
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#A9A9A9"
                            strokeWidth="2"
                          >
                            <path
                              d="M18 6L6 18M6 6l12 12"
                              strokeLinecap="round"
                            />
                          </svg>
                        )}
                      </span>
                    </td>
                    <td
                      className="px-6 py-4 text-sm text-right font-medium"
                      style={{ color: "#171717" }}
                    >
                      {store.session_count || 0}
                    </td>
                  </Link>
                ))}

                {stores.length === 0 && (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-6 py-12 text-center text-sm"
                      style={{ color: "#A9A9A9" }}
                    >
                      {brands.length === 0
                        ? "Add a brand first to create stores"
                        : "No stores configured yet"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Create Brand Modal */}
      {showCreateBrand && (
        <CreateBrandModal
          onClose={() => setShowCreateBrand(false)}
          onCreated={() => {
            setShowCreateBrand(false);
            loadData();
          }}
        />
      )}

      {/* Create Store Modal */}
      {showCreateStore && (
        <CreateStoreModal
          brands={brands}
          onClose={() => setShowCreateStore(false)}
          onCreated={() => {
            setShowCreateStore(false);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function CreateBrandModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [voiceName, setVoiceName] = useState("");
  const [voiceTone, setVoiceTone] = useState("friendly");
  const [primaryColor, setPrimaryColor] = useState("#000000");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch("/api/v1/config/brands", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          slug: slug || name.toLowerCase().replace(/\s+/g, "-"),
          voiceName: voiceName || "Assistant",
          voiceTone,
          primaryColor,
        }),
      });

      if (response.ok) {
        onCreated();
      }
    } catch (error) {
      console.error("Failed to create brand:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 w-full max-w-md">
        <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
          Add Brand
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Brand Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="e.g., Patagonia"
                required
              />
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Slug
              </label>
              <input
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="e.g., patagonia"
              />
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                AI Educator Name
              </label>
              <input
                type="text"
                value={voiceName}
                onChange={(e) => setVoiceName(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="e.g., Pat"
              />
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Voice Tone
              </label>
              <select
                value={voiceTone}
                onChange={(e) => setVoiceTone(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
              >
                <option value="friendly">Friendly</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="luxury">Luxury</option>
              </select>
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Primary Color
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="w-12 h-10 rounded border cursor-pointer"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                />
                <input
                  type="text"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  className="flex-1 px-3 py-2 border"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm"
              style={{ color: "#A9A9A9" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name}
              className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              {saving ? "Creating..." : "Create Brand"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateStoreModal({
  brands,
  onClose,
  onCreated,
}: {
  brands: Brand[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const [brandId, setBrandId] = useState(brands[0]?.id || "");
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await fetch("/api/v1/config/stores", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          brandId,
          name,
          city,
          state,
        }),
      });

      if (response.ok) {
        onCreated();
      }
    } catch (error) {
      console.error("Failed to create store:", error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white p-6 w-full max-w-md">
        <h2 className="text-lg font-medium mb-4" style={{ color: "#171717" }}>
          Add Store
        </h2>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Brand
              </label>
              <select
                value={brandId}
                onChange={(e) => setBrandId(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                required
              >
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                className="block text-xs uppercase tracking-wider mb-1"
                style={{ color: "#A9A9A9" }}
              >
                Store Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border"
                style={{ borderColor: "rgba(169,169,169,0.3)" }}
                placeholder="e.g., SoHo Flagship"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  className="block text-xs uppercase tracking-wider mb-1"
                  style={{ color: "#A9A9A9" }}
                >
                  City
                </label>
                <input
                  type="text"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  placeholder="e.g., New York"
                />
              </div>
              <div>
                <label
                  className="block text-xs uppercase tracking-wider mb-1"
                  style={{ color: "#A9A9A9" }}
                >
                  State
                </label>
                <input
                  type="text"
                  value={state}
                  onChange={(e) => setState(e.target.value)}
                  className="w-full px-3 py-2 border"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                  placeholder="e.g., NY"
                />
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full text-sm"
              style={{ color: "#A9A9A9" }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !name || !brandId}
              className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              {saving ? "Creating..." : "Create Store"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
